import { formatQuantity } from '../../utils/inventoryLabels';

/*
 * The options of a 409 `{error, candidates}`. The operator picks one and the
 * same request goes again with the field that candidate carries — and the
 * same idempotency key, because it is the same intent the server stopped
 * before moving anything.
 */

/**
 * The field a candidate answers with: the line, the stock bucket, the product
 * or the lot. A line candidate also names its product, so the line wins.
 *
 * @param {object} candidate
 * @returns {'line_id'|'bucket_id'|'product_id'|'lot'}
 */
export const candidateField = (candidate) => {
  if ('line_id' in candidate) return 'line_id';
  if ('bucket_id' in candidate) return 'bucket_id';
  if ('product_id' in candidate) return 'product_id';
  return 'lot';
};

const join = (parts) => parts.filter(Boolean).join(' · ');

/**
 * A candidate as a choice on screen.
 *
 * @param {object} candidate
 * @returns {{id: number|string, title: string, subtitle: string, badge: string|null, factor: number}}
 */
export const candidateOption = (candidate) => {
  const factor = Number(candidate.factor) || 1;
  const badge = factor > 1 ? `Caja ×${factor}` : null;

  switch (candidateField(candidate)) {
    case 'line_id':
      return {
        id: candidate.line_id,
        factor,
        badge,
        title: `${candidate.sku} — ${candidate.name}`,
        subtitle: join([
          candidate.line_no ? `Línea ${candidate.line_no}` : null,
          candidate.expected != null
            ? `${formatQuantity(candidate.received)} de ${formatQuantity(candidate.expected)} un`
            : null,
          candidate.pending != null ? `faltan ${formatQuantity(candidate.pending)} un` : null,
          candidate.lot ? `lote ${candidate.lot}` : null,
        ]),
      };
    case 'bucket_id':
      return {
        id: candidate.bucket_id,
        factor: 1,
        badge: null,
        title: candidate.from_slot_name || candidate.from_slot_code,
        subtitle: join([
          candidate.from_slot_code,
          candidate.lot ? `lote ${candidate.lot}` : null,
          `${formatQuantity(candidate.quantity)} un`,
        ]),
      };
    case 'product_id':
      return {
        id: candidate.product_id,
        factor,
        badge,
        title: `${candidate.sku} — ${candidate.name}`,
        subtitle: candidate.company_name || '',
      };
    default:
      return {
        id: candidate.lot ?? '',
        factor: 1,
        badge: null,
        title: candidate.lot ? `Lote ${candidate.lot}` : 'Sin lote',
        subtitle: `${formatQuantity(candidate.quantity)} un`,
      };
  }
};

/**
 * What a sheet needs to resend: which field the choice fills, and the options.
 *
 * @param {Array<object>} candidates
 */
export const describeCandidates = (candidates) => ({
  field: candidates.length > 0 ? candidateField(candidates[0]) : 'lot',
  options: candidates.map(candidateOption),
});
